import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
  trail: { x: number; y: number; alpha: number }[];
}

interface Rocket {
  x: number;
  y: number;
  targetY: number;
  vx: number;
  vy: number;
  color: string;
  exploded: boolean;
  trail: { x: number; y: number; alpha: number }[];
}

@Component({
  selector: 'app-fireworks',
  standalone: false,
  templateUrl: './fireworks.component.html',
  styleUrls: ['./fireworks.component.scss'],
})
export class FireworksComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() intensity: number = 0;
  @Input() label: string = '';

  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  visible: boolean = false;

  private ctx: CanvasRenderingContext2D | null = null;
  private rockets: Rocket[] = [];
  private particles: Particle[] = [];
  private rafId: number | null = null;
  private rocketsToLaunch: number = 0;
  private nextLaunchAt: number = 0;
  private endsAt: number = 0;

  private readonly palette = [
    '#ff5959', '#ffd166', '#06d6a0', '#118ab2', '#ef476f',
    '#fcbf49', '#f72585', '#7209b7', '#4cc9f0', '#80ed99',
  ];

  ngAfterViewInit(): void {
    this.ctx = this.canvasRef.nativeElement.getContext('2d');
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('intensity' in changes && this.intensity > 0) {
      this.startShow(this.intensity);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.handleResize);
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
  }

  private handleResize = (): void => {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
    this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  private startShow(intensity: number): void {
    const tier = Math.max(1, Math.min(6, Math.round(intensity)));
    const totalRockets = 6 + tier * 6;
    const durationMs = 2400 + tier * 600;

    this.rockets = [];
    this.particles = [];
    this.rocketsToLaunch = totalRockets;
    this.nextLaunchAt = performance.now();
    this.endsAt = performance.now() + durationMs;
    this.visible = true;

    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(this.frame);
    }
  }

  private frame = (now: number): void => {
    const ctx = this.ctx;
    const canvas = this.canvasRef?.nativeElement;
    if (!ctx || !canvas) {
      this.rafId = null;
      return;
    }

    const w = window.innerWidth;
    const h = window.innerHeight;

    if (this.rocketsToLaunch > 0 && now >= this.nextLaunchAt) {
      this.launchRocket(w, h);
      this.rocketsToLaunch -= 1;
      const minGap = 60;
      const maxGap = 220;
      this.nextLaunchAt = now + minGap + Math.random() * (maxGap - minGap);
    }

    ctx.fillStyle = 'rgba(10, 14, 30, 0.18)';
    ctx.fillRect(0, 0, w, h);

    for (const rocket of this.rockets) {
      rocket.x += rocket.vx;
      rocket.y += rocket.vy;
      rocket.vy += 0.04;
      rocket.trail.push({ x: rocket.x, y: rocket.y, alpha: 1 });
      if (rocket.trail.length > 8) rocket.trail.shift();

      if (!rocket.exploded && rocket.y <= rocket.targetY) {
        rocket.exploded = true;
        this.explode(rocket);
      }

      if (!rocket.exploded) {
        ctx.fillStyle = rocket.color;
        for (const t of rocket.trail) {
          ctx.globalAlpha = t.alpha;
          ctx.beginPath();
          ctx.arc(t.x, t.y, 2, 0, Math.PI * 2);
          ctx.fill();
          t.alpha *= 0.85;
        }
        ctx.globalAlpha = 1;
      }
    }
    this.rockets = this.rockets.filter((r) => !r.exploded);

    for (const p of this.particles) {
      p.vy += 0.05;
      p.vx *= 0.99;
      p.x += p.vx;
      p.y += p.vy;
      p.life -= 1;
      p.trail.push({ x: p.x, y: p.y, alpha: 1 });
      if (p.trail.length > 6) p.trail.shift();

      const lifeRatio = Math.max(p.life / p.maxLife, 0);
      ctx.fillStyle = p.color;
      for (let i = 0; i < p.trail.length; i++) {
        const t = p.trail[i];
        ctx.globalAlpha = t.alpha * lifeRatio * 0.6;
        ctx.beginPath();
        ctx.arc(t.x, t.y, p.size * 0.6, 0, Math.PI * 2);
        ctx.fill();
        t.alpha *= 0.78;
      }
      ctx.globalAlpha = lifeRatio;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    this.particles = this.particles.filter((p) => p.life > 0);

    const stillRunning = this.rockets.length > 0 || this.particles.length > 0 || this.rocketsToLaunch > 0;
    if (stillRunning && now < this.endsAt + 1500) {
      this.rafId = requestAnimationFrame(this.frame);
    } else {
      ctx.clearRect(0, 0, w, h);
      this.visible = false;
      this.rafId = null;
    }
  };

  private launchRocket(w: number, h: number): void {
    const x = w * (0.15 + Math.random() * 0.7);
    const targetY = h * (0.15 + Math.random() * 0.35);
    const color = this.palette[Math.floor(Math.random() * this.palette.length)];
    const flightDuration = 60 + Math.random() * 30;
    const vy = -(h - targetY) / flightDuration;
    const vx = (Math.random() - 0.5) * 0.6;
    this.rockets.push({
      x,
      y: h + 10,
      targetY,
      vx,
      vy,
      color,
      exploded: false,
      trail: [],
    });
  }

  private explode(rocket: Rocket): void {
    const tier = Math.max(1, Math.min(6, Math.round(this.intensity)));
    const count = 30 + tier * 20;
    const baseSpeed = 2 + tier * 0.4;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.1;
      const speed = baseSpeed * (0.6 + Math.random() * 0.6);
      const color =
        Math.random() < 0.7
          ? rocket.color
          : this.palette[Math.floor(Math.random() * this.palette.length)];
      const life = 60 + Math.floor(Math.random() * 40) + tier * 6;
      this.particles.push({
        x: rocket.x,
        y: rocket.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life,
        maxLife: life,
        color,
        size: 1.6 + Math.random() * 1.2,
        trail: [],
      });
    }
  }
}
